import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { Checkbox, Form, Select } from "semantic-ui-react";
import TourLayoutWrapper from "../components/TourLayoutWrapper";
import TourNav from "../constants/TourNav";
import TourStore from "~/stores/TourStore";
import ContentLoader from "~/components/element/ContentLoader";
import FormStore from "~/stores/FormStore";
import ToastMessageStore from "~/stores/ToastMessageStore";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import ModalUnsavedConfirmation from "~/components/element/ModalUnsavedConfirmation";
import SaveButton from "../components/SaveButton";
import StickyMenu from "~/components/StickyMenu";
import Loader from "../components/Loader";
import TourUiMeet3dLevelOptions from "~/constants/Tour/TourUiMeet3dLevelOptions";
import FormHelper from "~/utils/FormHelper";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import UiMeet3dLinkSegment from "~/modules/UiMeet3d/components/UiMeet3dLinkSegment";
import CustomerStore from "~/stores/CustomerStore";
import User from "~/models/User";

@observer
class TourUiMeet3dPage extends Component {
  @observable formStore = null;

  @observable customerStore = null;

  constructor(props) {
    super(props);
    document.title = "Tour - Meet";

    this.customerStore = new CustomerStore();
    this.customerStore.isLoading = true;
    this.formStore = new FormStore();
  }

  componentDidMount() {
    const { tourStore, user, showError } = this.props;
    const { tour } = tourStore;

    this.bindForm(tour);

    const { userId } = user;

    this.customerStore.load(userId).catch(error => showError(error.message));
  }

  @computed
  get customerUiMeet3dLevelDefaultText() {
    const { t, tourStore } = this.props;
    const { tour } = tourStore;

    if (typeof tour.customerDefaults !== "undefined" && typeof tour.customerDefaults.uiMeet3dLevel !== "undefined") {
      const defaultOption = TourUiMeet3dLevelOptions().find(
        option => option.value === tour.customerDefaults.uiMeet3dLevel,
      );

      if (typeof defaultOption === "undefined") {
        return tour.customerDefaults.uiMeet3dLevel;
      }

      return t(defaultOption.text);
    }

    return "";
  }

  save() {
    const { t, i18n, tourStore, toastMessageStore, tourId } = this.props;
    const { model } = this.formStore;

    tourStore
      .saveTour(
        tourId,
        {
          uiMeet3dLevel: FormHelper.convertToValue(model.uiMeet3dLevel),
          isUiMeet3dActive: model.isUiMeet3dActive,
        },
        i18n.language,
      )
      .then(() => {
        const { tour } = tourStore;

        this.bindForm(tour);

        toastMessageStore.addToastMessage(t("changesSaved"), "positive", t("success"));
      });
  }

  bindForm(tour) {
    const initialModel = {
      uiMeet3dLevel: FormHelper.convertToString(tour.uiMeet3dLevel),
      isUiMeet3dActive: tour.isUiMeet3dActive,
    };

    this.formStore = new FormStore(initialModel);
  }

  render() {
    const { tourStore, t, tourLayoutContentContainerRef, toastMessageStore } = this.props;
    const { tour, errorMessages } = tourStore;
    const { model, setProperty, isDirty, getAggregatedError } = this.formStore;
    const { isLoading: customerLoading } = this.customerStore;

    if (customerLoading || !tourStore.tourLoaded) {
      return <ContentLoader />;
    }

    // tourStore returns the validation errors under tour, strange
    const tourErrorMessages = errorMessages.tour;

    const validation = getValidationErrors({}, model);

    const isUiMeet3dActiveFormFieldError = getAggregatedError("isUiMeet3dActive", validation, tourErrorMessages);

    // For checkboxes, the error needs to point left
    if (
      typeof isUiMeet3dActiveFormFieldError === "object" &&
      typeof isUiMeet3dActiveFormFieldError.pointing !== "undefined"
    ) {
      isUiMeet3dActiveFormFieldError.pointing = "left";
    }

    return (
      <>
        <ModalUnsavedConfirmation when={isDirty} onSave={() => this.save()} onDiscard={() => this.bindForm(tour)} />
        <LoadingWrapper loading={tourStore.isSaving} message={t("saving")} />

        <StickyMenu
          contextRef={tourLayoutContentContainerRef}
          headerText={t("uiMeet3d")}
          button={<SaveButton disabled={!isDirty} onClick={() => this.save()} />}
        />

        <Loader active={tourStore.isLoading} page content={t("loading")} />

        <Form style={{ marginTop: "1rem" }}>
          <Form.Field
            name="uiMeet3dLevel"
            control={Select}
            label={t("clientPanelMisc:uiMeet3dLevel")}
            value={model.uiMeet3dLevel}
            options={TourUiMeet3dLevelOptions([
              "useDefaultWithValue",
              {
                defaultValue: this.customerUiMeet3dLevelDefaultText,
              },
            ]).map(mode => {
              return {
                key: mode.key,
                value: mode.value,
                text: typeof mode.text === "object" ? t(mode.text[0], mode.text[1]) : t(mode.text),
              };
            })}
            onChange={(_event, { value }) => setProperty("uiMeet3dLevel", value)}
            error={getAggregatedError("uiMeet3dLevel", validation, tourErrorMessages)}
          />
          <Form.Field
            name="isUiMeet3dActive"
            control={Checkbox}
            toggle
            label={t("clientPanelMisc:uiMeet3dActivated")}
            checked={model.isUiMeet3dActive}
            onChange={() => setProperty("isUiMeet3dActive", !model.isUiMeet3dActive)}
            error={isUiMeet3dActiveFormFieldError}
          />
        </Form>
        <UiMeet3dLinkSegment
          tourId={tour.tourId}
          toastMessageStore={toastMessageStore}
          actualUserStore={this.customerStore}
        />
      </>
    );
  }
}

TourUiMeet3dPage.propTypes = {
  t: PropTypes.func.isRequired,
  i18n: PropTypes.object.isRequired,
  tourId: PropTypes.number.isRequired,
  tourStore: PropTypes.instanceOf(TourStore),
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
  tourLayoutContentContainerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  user: PropTypes.instanceOf(User),
};

export default withTranslation()(ErrorWrapper(TourLayoutWrapper(TourUiMeet3dPage, TourNav.UI_MEET_3D)));
